import { useEffect, useMemo, useState } from "react";
import { Card } from "reactstrap";
import { useTranslation } from "react-i18next";
import StandardTable from "../../../ui/components/StandardTable";
import StandardTableFilterDropdown from "../../../ui/components/StandardTableFilterDropdown";
import EmptyState from "../../../components/EmptyState";
import Pagination from "../../../components/utils/Pagination";
import MaterialsCatalogHeader from "./MaterialsCatalogHeader";
import MaterialsCatalogRow from "./MaterialsCatalogRow";
import {
  DETECTION_CHARACTERIZATION,
  DETECTION_MONITORING,
  getCategoryOptions,
} from "./materialsCatalog";

const MaterialsCatalogList = ({
  materials,
  filteredMaterials,
  keywordFilter,
  onKeywordChange,
  categoryFilter,
  onCategoryChange,
  detectionFilter,
  onDetectionChange,
}) => {
  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState(1);
  // Référentiel consulté en lecture : on privilégie le scroll au feuilletage.
  const [recordPerPage, setRecordPerPage] = useState(100);
  const [resultPage, setResultPage] = useState(0);

  const categoryOptions = useMemo(
    () => getCategoryOptions(materials),
    [materials],
  );

  const detectionOptions = [
    {
      value: DETECTION_CHARACTERIZATION,
      label: t("materialsCatalog.scopeCharacterization"),
    },
    {
      value: DETECTION_MONITORING,
      label: t("materialsCatalog.scopeMonitoring"),
    },
  ];

  useEffect(() => {
    setCurrentPage(1);
  }, [
    keywordFilter,
    categoryFilter,
    detectionFilter,
    recordPerPage,
    materials,
  ]);

  const pagedMaterials = useMemo(() => {
    const start = (currentPage - 1) * recordPerPage;
    return filteredMaterials.slice(start, start + recordPerPage);
  }, [filteredMaterials, currentPage, recordPerPage]);

  const hasCatalog = materials.length > 0;
  const hasResults = filteredMaterials.length > 0;
  const hasActiveFilters = Boolean(
    keywordFilter.trim() || categoryFilter || detectionFilter,
  );

  return (
    <div className="settings-content-wrapper settings-waste-list materials-catalog-list">
      <div className="settings-list-shell">
        <Card className="table settings-waste-list__card border-0 shadow-none">
          <StandardTable
            fluidColCount={5}
            fixedColWidth={0}
            containerClassName={
              !hasCatalog ? "table-standard-container--list-empty" : undefined
            }
            columnAlignments={["left", "left", "left", "left", "right"]}
            topBar={
              hasCatalog ? (
                <section className="table-standard-top-bar">
                  <div className="table-standard-top-bar-inner">
                    <div className="flex gap-2 items-center flex-wrap flex-1 min-w-0">
                      <div className="search-input-wrapper">
                        <i
                          className="fas fa-search search-input-icon"
                          aria-hidden
                        />
                        <input
                          type="text"
                          className="search-input"
                          value={keywordFilter}
                          placeholder={t("materialsCatalog.searchPlaceholder")}
                          onChange={(e) => onKeywordChange(e.target.value)}
                        />
                      </div>
                      <StandardTableFilterDropdown
                        label={t("materialsCatalog.colCategory")}
                        value={categoryFilter}
                        options={categoryOptions}
                        allLabel={t("materialsCatalog.allCategories")}
                        onChange={onCategoryChange}
                      />
                      <StandardTableFilterDropdown
                        label={t("materialsCatalog.colScope")}
                        value={detectionFilter}
                        options={detectionOptions}
                        allLabel={t("materialsCatalog.allDetections")}
                        onChange={onDetectionChange}
                      />
                    </div>
                    <span className="materials-catalog-list__count">
                      {t("materialsCatalog.materialCount", {
                        count: filteredMaterials.length,
                      })}
                    </span>
                  </div>
                </section>
              ) : null
            }
            header={hasCatalog ? <MaterialsCatalogHeader /> : null}
            bottom={
              hasResults ? (
                <div className="table-standard-bottom table-pagination-bottom">
                  <Pagination
                    totalEntry={filteredMaterials.length}
                    recordPerPage={recordPerPage}
                    setRecordPerPage={setRecordPerPage}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    resultPage={resultPage}
                    setResultPage={setResultPage}
                  />
                </div>
              ) : null
            }
          >
            {hasResults ? (
              pagedMaterials.map((material) => (
                <MaterialsCatalogRow
                  key={material.referenceName}
                  material={material}
                />
              ))
            ) : hasCatalog && hasActiveFilters ? (
              <div className="table-standard-row table-standard-row--empty">
                <div
                  className="table-standard-row-cell"
                  style={{ gridColumn: "span 5" }}
                >
                  {t("No results match your search")}
                </div>
              </div>
            ) : (
              <EmptyState
                title={t("materialsCatalog.emptyTitle")}
                description={t("materialsCatalog.emptyDescription")}
              />
            )}
          </StandardTable>
        </Card>
      </div>
    </div>
  );
};

export default MaterialsCatalogList;
